import React, { useState, useRef, useEffect, useCallback, Suspense, lazy } from "react";
import { updateVisits } from "../../api/services/scheduling";
import { useQueryClient } from "react-query";
import { useSnackbar } from "notistack";
import { QK } from "../../base/qk";
import moment from "moment";
import { ClickAwayListener, MenuItem, MenuList, Paper } from "@mui/material";
import TaskTooltip from "./TaskTooltip";

const AllocateEmployeeModal = lazy(() => import("../PopupModal/AllocateEmployeeModal"));
const UnAllocateEmployeeModal = lazy(() => import("../PopupModal/UnAllocateEmployeeModal"));
const EditScheduleJobModal = lazy(() => import("../PopupModal/EditScheduleJobModal"));
const ChangeRunModal = lazy(() => import("../PopupModal/ChangeRunModal"));
const ConfirmModal = lazy(() => import("../ConfirmModal/ConfirmModal"));
const TaskDetailsModal = lazy(() => import("../PopupModal/TaskDetailsModal"));

const styleTag = document.createElement("style");
styleTag.innerHTML = `
  @keyframes stripes {
    from { background-position: 0 0; }
    to { background-position: 40px 0; }
  }
  .task-in-progress {
    background-image: linear-gradient(45deg, rgba(255,255,255,0.1) 25%, transparent 25%, transparent 50%, rgba(255,255,255,0.1) 50%, rgba(255,255,255,0.1) 75%, transparent 75%, transparent) !important;
    background-size: 30px 30px !important;
    animation: stripes 2s linear infinite;
  }
  .task-cancelled {
    background-image: repeating-linear-gradient(135deg, transparent, transparent 5px, rgba(0,0,0,0.03) 5px, rgba(0,0,0,0.03) 10px) !important;
    opacity: 0.7;
  }
  .task-completed {
    background-color: #f0fdf4 !important;
    border-left: 6px solid #22c55e !important;
    box-shadow: inset 0 0 15px rgba(34, 197, 94, 0.05) !important;
  }
  .task-completed::after {
    content: '✓';
    position: absolute;
    top: 6px;
    right: 10px;
    background: #22c55e;
    color: white;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 10px;
    font-weight: 900;
  }
  .task-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 12px rgba(0,0,0,0.1) !important;
  }
`;
document.head.appendChild(styleTag);

const DropTask = ({ task, type }) => {
  const [backgroundColor, setBackgroundColor] = useState("");
  const [showMenu, setShowMenu] = useState(false);
  const [showTaskDetailsModal, setShowTaskDetailsModal] = useState(false);
  const [showAllocateModal, setShowAllocateModal] = useState(false);
  const [showUnAllocateModal, setShowUnAllocateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showChangeRunModal, setShowChangeRunModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const menuRef = useRef(null);
  const tooltipRef = useRef(null);
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const isInProgress = task?.status === "In Progress";
  const isCancelled = task?.status === "Cancelled";
  const isCompleted = task?.status === "Completed";

  useEffect(() => {
    if (task?.shift_details?.shift_name) {
      setBackgroundColor(task.shift_details.shift_color);
    } else {
      setBackgroundColor("#F7EF46");
    }
  }, [task?.shift_details?.shift_name, task?.shift_details?.shift_color]);

  const handleRightClick = (e) => {
    e.preventDefault();
    setShowMenu(true);
  };
  const handleShowTaskModal = () => {
    setShowMenu(false);
    setShowTaskDetailsModal(true);
  };
  const handleCloseTaskModal = () => setShowTaskDetailsModal(false);
  const handleAllocateClick = () => {
    setShowMenu(false);
    setShowAllocateModal(true);
  };
  const handleUnAllocateClick = () => {
    setShowMenu(false);
    setShowUnAllocateModal(true);
  };
  const handleShowEditModal = () => {
    setShowMenu(false);
    setShowEditModal(true);
  };
  const handleCloseEditModal = () => setShowEditModal(false);
  const handleShowChangeRunModal = () => {
    setShowMenu(false);
    setShowChangeRunModal(true);
  };
  const handleShowConfirmModal = () => {
    setShowMenu(false);
    setShowConfirmModal(true);
  };
  const handleCloseShowConfirmModal = () => setShowConfirmModal(false);

  const handleUnallocateJob = async () => {
    try {
      const data = { is_on_right: false, run: null };
      const response = await updateVisits(task.id, data);
      if (response.status === 200) {
        queryClient.invalidateQueries([QK.runs]);
        queryClient.invalidateQueries([QK.unAssignedTasks]);
        enqueueSnackbar("Unallocated Successfully", { variant: "success" });
      }
    } catch (error) {
      enqueueSnackbar("Failed to Unallocate", { variant: "error" });
    }
    handleCloseShowConfirmModal();
  };

  const handleCloseChangeRunModal = () => setShowChangeRunModal(false);
  const handleCloseModal = () => setShowAllocateModal(false);
  const handleUnAllocateCloseModal = () => setShowUnAllocateModal(false);
  const handleMouseEnter = () => setShowTooltip(true);
  const handleMouseLeave = () => setShowTooltip(false);

  const handleClickOutside = useCallback((e) => {
    if (menuRef.current && !menuRef.current.contains(e.target))
      setShowMenu(false);
  }, []);

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [handleClickOutside]);

  const rawStart = type === "template" 
    ? task?.time_required_from 
    : (task?.start_time || task?.time_required_from);

  const rawEnd = type === "template" 
    ? task?.time_required_to 
    : (task?.end_time || task?.time_required_to);

  const startTime = rawStart
    ? moment(rawStart, ["YYYY-MM-DDTHH:mm:ssZ", "HH:mm:ss"]).format("HH:mm")
    : "--:--";
  const endTime = rawEnd
    ? moment(rawEnd, ["YYYY-MM-DDTHH:mm:ssZ", "HH:mm:ss"]).format("HH:mm")
    : "--:--";

  const calculateDuration = (from, to) => {
    if (!from || !to) return "0m";
    const start = moment(from, ["YYYY-MM-DDTHH:mm:ssZ", "HH:mm:ss"]);
    const end = moment(to, ["YYYY-MM-DDTHH:mm:ssZ", "HH:mm:ss"]);
    const duration = moment.duration(end.diff(start));
    const hours = Math.floor(duration.asHours());
    const minutes = duration.minutes();
    return `${hours > 0 ? hours + "h " : ""}${
      minutes > 0 ? minutes + "m" : ""
    }`.trim();
  };

  const getTotalMinutes = (from, to) => {
    if (!from || !to) return 0;
    const format = ["YYYY-MM-DDTHH:mm:ssZ", "HH:mm"];
    const start = moment(from, format);
    const end = moment(to, format);
    if (end.isBefore(start)) end.add(1, "days");
    return Math.round(moment.duration(end.diff(start)).asMinutes());
  };

  const contractDuration = calculateDuration(rawStart, rawEnd);
  const isRescheduled = !!task.rescheduled_start;
  const reschedTimeFrom = isRescheduled
    ? moment(task.rescheduled_start, [
        "YYYY-MM-DDTHH:mm:ssZ",
        "HH:mm:ss",
      ]).format("HH:mm")
    : null;
  const reschedTimeTo = isRescheduled
    ? moment(task.rescheduled_end, ["YYYY-MM-DDTHH:mm:ssZ", "HH:mm:ss"]).format(
        "HH:mm"
      )
    : null;
  const reschedTotalMins = isRescheduled
    ? getTotalMinutes(task.rescheduled_start, task.rescheduled_end)
    : null;

  return (
    <div
      style={{ position: "relative" }}
      onContextMenu={handleRightClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div
        onClick={handleShowTaskModal}
        className={`task-card ${isInProgress ? "task-in-progress" : ""} ${
          isCancelled ? "task-cancelled" : ""
        } ${isCompleted ? "task-completed" : ""}`}
        style={{
          backgroundColor: isCancelled
            ? "#f1f5f9"
            : isCompleted
            ? "#f0fdf4"
            : backgroundColor,
          color: isCancelled ? "#64748b" : isCompleted ? "#15803d" : "black",
          padding: "10px",
          borderRadius: "8px",
          borderLeft: isCancelled
            ? "6px solid #9e9e9e"
            : isCompleted
            ? "6px solid #22c55e"
            : !task?.is_assigned
            ? "6px solid #d32f2f"
            : "6px solid rgba(0,0,0,0.1)",
          cursor: isCancelled ? "not-allowed" : "pointer",
          boxShadow: "0 2px 4px rgba(0,0,0,0.08)",
          transition: "all 0.2s ease",
          marginBottom: "4px",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div className="d-flex justify-content-between align-items-start">
          <div style={{ flex: 1, minWidth: 0 }}>
            <p
              className="fw-bold m-0 text-truncate"
              style={{
                fontSize: "13px",
                lineHeight: "1.2",
                textDecoration: isCancelled ? "line-through" : "none",
              }}
            >
              {task?.visit_name}
              {isInProgress && (
                <span style={{ marginLeft: "8px", color: "#2e7d32" }}>●</span>
              )}
            </p>
            <span
              style={{
                fontSize: "9px",
                textTransform: "uppercase",
                opacity: 0.6,
              }}
            >
              {isCancelled
                ? "CANCELLED"
                : isCompleted
                ? "COMPLETED"
                : task?.run_type || "Standard"}
            </span>
          </div>
          {task?.time_critical && !isCancelled && !isCompleted && (
            <span title="Time Critical" style={{ fontSize: "12px" }}>
              🔴
            </span>
          )}
        </div>

        <p className="m-0 mt-1" style={{ fontSize: "11px", fontWeight: "700" }}>
          {isRescheduled ? (
            <span
              style={{
                color: isCancelled
                  ? "#9e9e9e"
                  : isCompleted
                  ? "#166534"
                  : "#c53030",
              }}
            >
              🕒 {reschedTimeFrom} - {reschedTimeTo} ({reschedTotalMins}m)
            </span>
          ) : (
            <span
              style={{
                color: isCompleted ? "rgba(22,101,52,0.8)" : "rgba(0,0,0,0.6)",
              }}
            >
              {startTime} | {contractDuration} | {endTime}
            </span>
          )}
        </p>

        <div className="d-flex justify-content-between align-items-center mt-2">
          <p
            className="m-0 text-truncate"
            style={{ maxWidth: "110px", fontSize: "12px", fontWeight: "500" }}
          >
            {task?.client}
          </p>
          <span
            style={{
              fontSize: "10px",
              background: "rgba(255,255,255,0.4)",
              padding: "1px 5px",
              borderRadius: "4px",
              border: "1px solid rgba(0,0,0,0.1)",
            }}
          >
            {task?.employee_assigned}/{task?.carers_required} 👥
          </span>
        </div>
      </div>

      {showTooltip && (
        <TaskTooltip
          task={task}
          isRescheduled={isRescheduled}
          reschedTimeFrom={reschedTimeFrom}
          reschedTimeTo={reschedTimeTo}
          reschedTotalMins={reschedTotalMins}
          startTime={startTime}
          endTime={endTime}
          getTotalMinutes={getTotalMinutes}
          rawStart={rawStart}
          rawEnd={rawEnd}
        />
      )}

      {showMenu && (
        <ClickAwayListener onClickAway={() => setShowMenu(false)}>
          <Paper
            elevation={8}
            sx={{
              position: "absolute",
              zIndex: 1200,
              borderRadius: 2,
              minWidth: 200,
              overflow: "hidden",
            }}
          >
            <MenuList sx={{ py: 0 }}>
              <MenuItem
                onClick={handleAllocateClick}
                sx={{ fontSize: "13px", py: 1.2 }}
              >
                Allocate Employee
              </MenuItem>
              <MenuItem
                onClick={handleUnAllocateClick}
                sx={{ fontSize: "13px", py: 1.2 }}
              >
                Unallocate Employee
              </MenuItem>
              {type === "schedule" && (
                <>
                  <MenuItem
                    onClick={handleShowEditModal}
                    sx={{ fontSize: "13px", py: 1.2 }}
                  >
                    Edit Time
                  </MenuItem>
                  <MenuItem
                    onClick={handleShowChangeRunModal}
                    sx={{ fontSize: "13px", py: 1.2 }}
                  >
                    Change Run
                  </MenuItem>
                </>
              )}
              {type === "template" && (
                <MenuItem
                  onClick={handleShowConfirmModal}
                  sx={{ fontSize: "13px", py: 1.2, color: "error.main" }}
                >
                  Unallocate Job
                </MenuItem>
              )}
            </MenuList>
          </Paper>
        </ClickAwayListener>
      )}

{/* Modal Section */}
      <Suspense fallback={null}>
        {showAllocateModal && (
          <AllocateEmployeeModal
            show={showAllocateModal}
            startDate={rawStart}
            endDate={rawEnd}
            id={task.job_id || task.id}
            area_id={task.area_id}
            type={type}
            employeeList={task.employee_list || task.employee_lists}
            employeeNumber={task.carers_required - task.employee_assigned}
            handleClose={handleCloseModal}
          />
        )}

        {showUnAllocateModal && (
          <UnAllocateEmployeeModal
            show={showUnAllocateModal}
            handleClose={handleUnAllocateCloseModal}
            assigned_employee_list={
              task.assigned_employee_details || task.employee_details
            }
            employee_list={task.employee_list || task.employee_lists}
            id={task.job_id || task.id}
            type={type}
          />
        )}

        {showEditModal && (
          <EditScheduleJobModal
            show={showEditModal}
            handleClose={handleCloseEditModal}
            id={task.job_id}
          />
        )}

        {showChangeRunModal && (
          <ChangeRunModal
            show={showChangeRunModal}
            handleClose={handleCloseChangeRunModal}
            id={task.job_id}
          />
        )}

        {showConfirmModal && (
          <ConfirmModal
            show={showConfirmModal}
            handleClose={handleCloseShowConfirmModal}
            handleConfirm={handleUnallocateJob}
            body="Are you sure want to unallocate this job?"
            title="Confirm Unallocate"
          />
        )}

        {showTaskDetailsModal && (
          <TaskDetailsModal
            show={showTaskDetailsModal}
            handleClose={handleCloseTaskModal}
            id={task.job_id || task.id}
          />
        )}
      </Suspense>
    </div>
  );
};

export default DropTask;
